import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getAvailableDurationPackageOptions,
  getDurationPackageOptions,
  areRatesLoading,
} from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { getCurrentUserId, selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { generateAccommodationCheckoutItems, generateVillaCheckoutItem } from 'lib/checkout/accommodation/cart'
import GeoContext from 'contexts/geoContext'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageBurnSelectionContext } from 'contexts/OfferPage/OfferPageBurnSelectionContext'
import PurchaseContextChangedMessage from 'components/OfferPage/PurchaseContextChangedMessage'
import { getFlashPackageQuoteContext, isFlashSameRoomPackageAvailable, requiresFlashMultiRoomAssistance } from 'lib/offer/flashPurchasePolicy'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getAvailableRoomsByPackage, getUpgradeablePackage, mapClientOfferToServerOffer, mapClientPackageToServerPackage, mapRoomOccupantsToServerOccuppancies } from '../utils'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { isFlashHotelOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import ModalContext from 'contexts/ModalContext'
import useFlashOfferSignUpModal from '../FlashHotel/useFlashOfferSignUpModal'
import { getUpgradeItemContext, upgradeEvent } from 'analytics/snowplow/events'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PackageUpgradeModal from '../PackageUpgrade/PackageUpgradeModal'
import moment from 'moment'
import { checkoutStateIsReady } from 'middlewares/analyticsMiddleware'
import getSelectedLoyaltyUpgrade from '../offerPageUtils/getSelectedLoyaltyTargetUpgradePackage'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { isRegionFilterBypassedForOffer } from 'selectors/marketingSelectors'
import { getUpgradeablePackage as getUpgradeablePackageLib } from '@luxuryescapes/lib-room-upgrade'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import CurrencyContext from 'contexts/currencyContext'
import buildPrefetchTopOfferFilters, { isExperienceListStale } from 'lib/experiences/buildPrefetchTopOfferFilters'
import { fetchExperienceList, type ExperienceListFilters } from 'actions/ExperienceActions'
import { shouldPrefetchExperiencesInFlow, isOneClickExperienceTransferEnabled } from 'checkout/selectors/experienceSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { buildOfferPageCartAttributes, DEFAULT_FUNNEL_FEATURES } from 'checkout/selectors/funnel'
import { refreshFunnelOptimiserFlags } from 'actions/SystemActions'
import { useSeatEligiblePassengers } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { getRoundTripSeatsLeft, hasInsufficientCharterSeats } from 'lib/charterFlights/charterFlightAvailability'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useIntl } from 'react-intl'
import useExperiment from 'hooks/Experiments/useExperiment'
import {
  isInternationalCheckoutFlowEligibleOfferType,
  isInternationalCheckoutFlowEligibleRegion,
  isInternationalCheckoutFlowEligibleUser,
} from 'lib/checkout/internationalCheckoutFlow'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isSpoofed } from 'selectors/isSpoofed'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'
import useUserCanApplyLoyaltyBenefit from 'loyalty/hooks/useUserCanApplyLoyaltyBenefit'

interface GenerateCartOptions {
  packageOption: App.OfferPackageOptionView | undefined
  flights: boolean
  upgradePackageOption?: App.OfferPackageOptionView
  originalPackageOption?: App.OfferPackageOptionView
  loyaltyAppliedUpgradeType?: App.LoyaltyStayUpgradeType
  basePackageId?: string
}

interface Props extends ComponentProps<typeof TextButton> {
  airport?: App.Airport
  checkIn?: string
  checkOut?: string
  duration?: number
  isLoggedIn: boolean
  customerId?: string
  offer: App.HotelOffer | App.VillaOffer
  packageOptions: Array<App.OfferPackageOptionView>
  availablePackageOptions: Array<App.OfferPackageOptionView>
  packageView: App.OfferPackageOptionView
  rooms: Array<App.RoomOccupants>
  onSubmit?: (e: React.MouseEvent<HTMLButtonElement>) => void
  reserveForZero?: boolean
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>
  requireCommittedSameRoomSelection?: boolean
  ratesLoading?: boolean
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>) => void
  upgradeEnabled?: boolean
  loyaltyTargetRoomUpgradesSelected?: App.LoyaltyTargetStayUpgradesSelected
  isPackageUpgradeLibEnabled: boolean
  disablePrefetchExperiences?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FareClassWithReward
  bypassRegionFilter?: boolean
  bypassRegionSettingId?: string
}

export function OfferCheckoutButton(props: Props) {
  const {
    children,
    packageView,
    rooms,
    isLoggedIn,
    customerId,
    checkIn,
    checkOut,
    duration,
    packageOptions,
    availablePackageOptions,
    offer,
    airport,
    onSubmit,
    onProceedToBooking,
    upgradeEnabled = true,
    loyaltyTargetRoomUpgradesSelected,
    isPackageUpgradeLibEnabled,
    disablePrefetchExperiences = false,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    disabled,
    requireCommittedSameRoomSelection = false,
    ratesLoading,
    bypassRegionFilter,
    bypassRegionSettingId,
    ...rest
  } = props
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const isMobile = useScreenSizeOnly('mobile')
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const showFlashOfferSignUpModal = useFlashOfferSignUpModal()
  const { isAnyVariant: prefetchEnabled, triggerActivation: triggerExperiencesAnalytics } = useExperiment(Experiments.croPrefetchInflowCheckoutExperiences, { canActivate: false })
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  // Loyalty upgrades: every room booked here gets the upgrade, so the member needs one left per room
  const hasLoyaltyUpgradePerRoom = useUserCanApplyLoyaltyBenefit('hotel_upgrade', rooms.length)
  const numberOfPurchases = useAppSelector(state => state.auth.account.numberOfPurchases)
  const spoofed = useAppSelector(isSpoofed)
  const canActivateInternationalCheckoutFlow = isInternationalCheckoutFlowEligibleRegion(currentRegionCode) &&
    isInternationalCheckoutFlowEligibleOfferType(offer.type) &&
    isInternationalCheckoutFlowEligibleUser({ canRedeemLuxPlusBenefits, numberOfPurchases, isSpoofed: spoofed })
  const { isAnyVariant: isInternationalCheckoutFlowBucketed } = useExperiment(
    Experiments.internationalCheckoutFlow,
    {
      canActivate: canActivateInternationalCheckoutFlow,
    },
  )
  const isInternationalCheckoutFlowTreatment = canActivateInternationalCheckoutFlow &&
    isInternationalCheckoutFlowBucketed
  const canPrefetchInFlow = useAppSelector(state => shouldPrefetchExperiencesInFlow(state, offer, disablePrefetchExperiences))
  const experienceLists = useAppSelector(state => state.experience.experienceLists)
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const geoCoords = useMemo(() => ({ latitude: offer?.property?.latitude, longitude: offer?.property?.longitude }), [offer?.property?.latitude, offer?.property?.longitude])
  const oneClickTransferEnabled = useAppSelector(state => isOneClickExperienceTransferEnabled(state))
  // We are passed a package view, but that's not always guaranteed to be the 'latest' one
  // e.g. a `selectedPackageView` doesn't include price updates based on date selection (maybe there was a surcharge?)
  // So to get around this, we always use the 'latest' calculated view for the unique key we're given
  const latestPackageView = useMemo(() => packageOptions.find(option => option.package.uniqueKey === packageView.package.uniqueKey), [packageOptions, packageView.package.uniqueKey])
  const sameRoomPurchase = requireCommittedSameRoomSelection && isFlashHotelOffer(offer) && rooms.length > 1 && offerPageState.multiRoomMode === 'same-room'
  const requiresSameRoomRates = !!offer.property?.useDynamicCancellationPolicies && !!checkIn
  const availableUpgradePackageOptions = useMemo(() => sameRoomPurchase ?
      availablePackageOptions.filter(option => isFlashSameRoomPackageAvailable(option, rooms.length, requiresSameRoomRates, !!checkIn)) :
    availablePackageOptions, [availablePackageOptions, checkIn, requiresSameRoomRates, rooms.length, sameRoomPurchase])
  const { quote: selectionBurnQuote } = useContext(OfferPageBurnSelectionContext)
  const selectionBurnReady = !selectionBurnQuote || selectionBurnQuote.ready
  const latestBurnReady = useRef(selectionBurnReady)
  latestBurnReady.current = selectionBurnReady
  const sameRoomReady = selectionBurnReady && !ratesLoading && !!latestPackageView &&
    isFlashSameRoomPackageAvailable(latestPackageView, rooms.length, requiresSameRoomRates, !!checkIn) &&
    !requiresFlashMultiRoomAssistance({ roomCount: rooms.length, canRedeemLuxPlusBenefits, hotelMemberTotal: latestPackageView.hotelMemberTotal }) &&
    (!requireCommittedSameRoomSelection || (offerPageState.flashSameRoomSelection?.offerId === offer.id && offerPageState.flashSameRoomSelection.packageKey === latestPackageView.package.uniqueKey))
  const purchaseContextKey = useMemo(() => {
    if (!requireCommittedSameRoomSelection) return undefined
    return JSON.stringify([
      offer.id, offerPageState.multiRoomMode, rooms, checkIn, checkOut, duration, currentCurrency,
      getFlashPackageQuoteContext(latestPackageView), availablePackageOptions.map(getFlashPackageQuoteContext),
      loyaltyTargetRoomUpgradesSelected, offerPageState.flights ? airport?.code : undefined, fareClass, charterBundleRoute, charterBundleEnabled, offerPageState.flights,
      offerPageState.pointsToApply, canRedeemLuxPlusBenefits,
      offerPageState.flashSameRoomSelection,
    ])
  }, [airport?.code, availablePackageOptions, canRedeemLuxPlusBenefits, charterBundleEnabled, charterBundleRoute, checkIn, checkOut, currentCurrency, duration, fareClass, latestPackageView, loyaltyTargetRoomUpgradesSelected, offer.id, offerPageState.flashSameRoomSelection, offerPageState.flights, offerPageState.multiRoomMode, offerPageState.pointsToApply, requireCommittedSameRoomSelection, rooms])
  const currentPurchase = useRef({ key: purchaseContextKey, customerId, ready: sameRoomReady, selection: offerPageState.flashSameRoomSelection })
  const mounted = useRef(true)
  const purchaseInFlight = useRef(false)
  /** First authenticated owner of the in-flight intent. Kept apart from the refreshed customer id. */
  const purchaseIntentOwnerId = useRef<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const [purchaseContextChanged, setPurchaseContextChanged] = useState(false)
  useEffect(() => {
    currentPurchase.current = { key: purchaseContextKey, customerId, ready: sameRoomReady, selection: offerPageState.flashSameRoomSelection }
    if (!purchaseInFlight.current) return
    // Lock the first authenticated customer as soon as it appears, including while login is still open.
    if (!purchaseIntentOwnerId.current) {
      if (customerId) purchaseIntentOwnerId.current = customerId
      return
    }
    if (purchaseIntentOwnerId.current !== customerId && mounted.current) setPurchaseContextChanged(true)
  }, [customerId, offerPageState.flashSameRoomSelection, purchaseContextKey, sameRoomReady])
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const canContinuePurchase = useCallback(() => {
    if (!latestBurnReady.current) {
      if (mounted.current) setPurchaseContextChanged(true)
      return false
    }
    if (!sameRoomPurchase && !requireCommittedSameRoomSelection) return true
    const current = currentPurchase.current
    // Anonymous intent takes its owner from the first authenticated customer, then rejects a later account or logout.
    if (purchaseInFlight.current && !purchaseIntentOwnerId.current && current.customerId) {
      purchaseIntentOwnerId.current = current.customerId
    }
    const valid = mounted.current && current.ready && current.key === purchaseContextKey &&
      (!purchaseIntentOwnerId.current || current.customerId === purchaseIntentOwnerId.current) &&
      (!current.selection || (current.selection.offerId === offer.id && current.selection.packageKey === packageView.package.uniqueKey))
    if (!valid && mounted.current) setPurchaseContextChanged(true)
    return valid
  }, [offer.id, packageView.package.uniqueKey, purchaseContextKey, requireCommittedSameRoomSelection, sameRoomPurchase])
  const {
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const effectiveCharterBundleRoute = useMemo(
    () => (charterBundleEnabled ? charterBundleRoute : undefined),
    [charterBundleEnabled, charterBundleRoute],
  )
  const baseFareClass = toBaseFareClass(fareClass)

  const {
    departingJourney: departingCharterFlightJourney,
    returningJourney: returningCharterFlightJourney,
    departingFareFamily: departingCharterFareFamily,
    returningFareFamily: returningCharterFareFamily,
    searchResultsTimestamp: charterFlightSearchResultsTimestamp,
    providerSearchId: charterFlightSearchId,
    fetching: charterFlightsFetching,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: effectiveCharterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass: baseFareClass,
    feederOriginAirportCode: airport?.code,
  })

  const awaitingCharterBundleData = !!effectiveCharterBundleRoute && charterFlightsFetching

  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const charterSeatsLeft = useMemo(() => getRoundTripSeatsLeft(departingCharterFlightJourney, returningCharterFlightJourney), [departingCharterFlightJourney, returningCharterFlightJourney])
  const insufficientCharterSeats = !!effectiveCharterBundleRoute && hasInsufficientCharterSeats(checkIn, checkOut, duration, charterSeatsLeft, seatEligiblePassengers)

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: duration,
    charterBundleRoute: effectiveCharterBundleRoute,
    rooms,
    fareClass: baseFareClass,
    feederOriginAirportCode: airport?.code,
  })

  // For charter bundle, check if checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !effectiveCharterBundleRoute || (!!checkIn && !!charterDepartDate)

  const generateCart = useCallback(({
    packageOption,
    flights,
    upgradePackageOption,
    originalPackageOption,
    loyaltyAppliedUpgradeType,
    basePackageId,
  }: GenerateCartOptions) => {
    if (!packageOption || !checkIn || !canContinuePurchase()) { return }
    if (sameRoomPurchase && !isFlashSameRoomPackageAvailable(packageOption, rooms.length, requiresSameRoomRates, true)) {
      setPurchaseContextChanged(true)
      return
    }

    const checkOut = moment(checkIn).add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT)
    const dates = { checkIn, checkOut }

    let accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem> = []
    if (offer.type !== 'rental') {
      accommodationItems = generateAccommodationCheckoutItems({
        offer,
        packageOption,
        dates,
        roomOccupants: rooms,
        currencyCode: currentCurrency,
        loyaltyAppliedUpgradeType,
        basePackageId,
        bypassRegionFilter,
        bypassRegionSettingId,
      })
    }

    let villaItems: Array<App.Checkout.VillaItem> = []
    if (offer.type === 'rental') {
      villaItems = [generateVillaCheckoutItem(offer, packageOption, dates, rooms[0], currentCurrency)]
    }

    const flightItems = flights && airport ? generateAccommodationBundleFlightItems({
      rooms,
      airport,
      offer,
      accommodationItems,
      quotedFare: flightPrice,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney,
      returningCharterFlightJourney,
      departingCharterFareFamily,
      returningCharterFareFamily,
      charterFlightSearchResultsTimestamp,
      charterFlightSearchId,
    }) : []

    if (upgradePackageOption && originalPackageOption && accommodationItems.length === 1) {
      checkoutStateIsReady().then(() => trackEvent(
        upgradeEvent({
          hasUpgraded: packageOption.package.uniqueKey === upgradePackageOption.package.uniqueKey,
          chosenCartItemId: accommodationItems[0].itemId,
        },
        getUpgradeItemContext(originalPackageOption, upgradePackageOption, accommodationItems[0]),
        ['hotel'],
        ),
      ),
      )
    }
    if (sameRoomPurchase || requireCommittedSameRoomSelection) modalElementContext?.resolve()
    onProceedToBooking([...accommodationItems, ...villaItems, ...flightItems])
  }, [canContinuePurchase, modalElementContext, requireCommittedSameRoomSelection, requiresSameRoomRates, sameRoomPurchase, checkIn, offer, airport, rooms, flightPrice, charterBundleRoute, fareClass, departingCharterFlightJourney, returningCharterFlightJourney, departingCharterFareFamily, returningCharterFareFamily, charterFlightSearchResultsTimestamp, charterFlightSearchId, onProceedToBooking, currentCurrency, bypassRegionFilter, bypassRegionSettingId])

  const onBookNow = useCallback(async(packageOption: App.OfferPackageOptionView, flights: boolean) => {
    // we're booking, close the filters
    offerPageDispatch({ type: OfferPageActions.closeFilter })
    // A same-room continuation keeps its owner mounted until the final quote check.
    if (!sameRoomPurchase && !requireCommittedSameRoomSelection) modalElementContext?.resolve()

    // Refresh funnel optimiser flags with cart attributes and UTMs before upgrade modal decision
    const cartAttributes = buildOfferPageCartAttributes({
      totalPrice: flights ? packageOption.hotelPrice + packageOption.flightPrice : packageOption.hotelPrice,
      destinationCountry: offer.property?.geoData?.country,
      rooms,
      checkIn,
      duration: packageOption.package.duration,
      offerId: offer.id,
      productType: 'hotel',
    })
    const currentFunnelFeatures = (await dispatch(refreshFunnelOptimiserFlags(cartAttributes))) ?? DEFAULT_FUNNEL_FEATURES
    if (!canContinuePurchase()) return

    const selectedLoyaltyUpgrade = hasLoyaltyUpgradePerRoom ? getSelectedLoyaltyUpgrade(
      packageOption,
      availableUpgradePackageOptions,
      loyaltyTargetRoomUpgradesSelected,
    ) : undefined

    if (selectedLoyaltyUpgrade) {
      fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, selectedLoyaltyUpgrade.packageOptionView.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
      generateCart({
        packageOption,
        flights,
        loyaltyAppliedUpgradeType: selectedLoyaltyUpgrade.upgradeType,
        basePackageId: packageOption.package.id,
      })

      return
    }

    let upgradeAvailable: App.OfferPackageOptionView | undefined
    if (
      upgradeEnabled &&
      currentFunnelFeatures.roomUpgrade &&
      isEnabledForFeature(config.PACKAGE_UPGRADE_MODAL_ENABLED_LIST, offer.type) &&
      availableUpgradePackageOptions.length > 0 &&
      checkIn
    ) {
      if (isPackageUpgradeLibEnabled && isFlashHotelOffer(offer)) {
        // Mapping to lib-rooms-upgrade types
        const serverOffer = mapClientOfferToServerOffer(offer, packageOptions)
        const serverPackage = mapClientPackageToServerPackage(packageOption.package)
        const occupancies = mapRoomOccupantsToServerOccuppancies(rooms)
        const avaiableRoomsByPackage = getAvailableRoomsByPackage(packageOptions)
        const result = getUpgradeablePackageLib(
          serverOffer,
          serverPackage,
          occupancies,
          avaiableRoomsByPackage,
        )
        // Find the original package option that corresponds to the upgradable package
        upgradeAvailable = result ?
            availableUpgradePackageOptions.find(option => option.package.uniqueKey === result.package.uniqueKey) :
          undefined
      } else {
        upgradeAvailable = getUpgradeablePackage(
          availableUpgradePackageOptions,
          packageOption,
          moment(checkIn),
          isLpcHotelOffer(offer),
          offer.property.timezoneOffset,
        )
      }
    }

    if (upgradeAvailable && !isInternationalCheckoutFlowTreatment) {
      trackOfferPageEvent('UPGRADE_MODAL_OPENED')
      trackClientEvent({
        subject: 'upgrade_modal',
        action: 'opened',
        category: 'logging',
        type: 'nonInteraction',
      })
      const upgradeModal = <PackageUpgradeModal
        upgradePackageView={upgradeAvailable}
        basePackageView={packageOption}
        hasFlights={flights}
        offer={offer}
        checkIn={checkIn}
      />
      const onUpgradeSelected = (resolvedPackageOption?: App.OfferPackageOptionView) => {
        if (resolvedPackageOption && canContinuePurchase()) {
          fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, resolvedPackageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
          generateCart({
            packageOption: resolvedPackageOption,
            flights,
            upgradePackageOption: upgradeAvailable,
            originalPackageOption: packageOption,
          })
        }
      }
      if (requireCommittedSameRoomSelection) {
        onUpgradeSelected(await showModal<App.OfferPackageOptionView | undefined>(upgradeModal))
      } else {
        // Preserve Control's callback settlement while the optional upgrade modal remains open.
        showModal<App.OfferPackageOptionView | undefined>(upgradeModal).then(onUpgradeSelected)
      }
      return
    }

    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, packageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
    generateCart({ packageOption, flights })
  }, [canContinuePurchase, requireCommittedSameRoomSelection, sameRoomPurchase, offerPageDispatch, modalElementContext, availableUpgradePackageOptions, loyaltyTargetRoomUpgradesSelected, hasLoyaltyUpgradePerRoom, offer, upgradeEnabled, checkIn, currentCurrency, rooms, generateCart, isPackageUpgradeLibEnabled, packageOptions, trackOfferPageEvent, showModal, dispatch, isInternationalCheckoutFlowTreatment])

  const invalidRoomSelection = !hasSelectedRooms(rooms) || isRoomsInvalid(rooms)

  const onBookNowClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    if (requireCommittedSameRoomSelection && purchaseInFlight.current) return
    const isFlightBuyType = e.currentTarget.dataset.buytype === 'flights'
    const shouldPrefetch = canPrefetchInFlow && !isFlightBuyType && !invalidRoomSelection && !!checkIn && !!packageView

    if (prefetchEnabled && shouldPrefetch) {
      const prefetchFilters = buildPrefetchTopOfferFilters({
        currencyCode: currentCurrency,
        offer,
        checkIn,
        checkOut,
        durationDays: duration,
        rooms,
        categories,
        geoCoords,
        oneClickTransferEnabled,
      })

      prefetchFilters.forEach((f: ExperienceListFilters) => {
        if (isExperienceListStale(experienceLists, f)) {
          dispatch(fetchExperienceList(f))
        }
      })
      triggerExperiencesAnalytics()
    }
    if (!latestPackageView) return
    if ((sameRoomPurchase || requireCommittedSameRoomSelection) && !sameRoomReady) {
      e.preventDefault()
      setPurchaseContextChanged(true)
      return
    }
    onSubmit?.(e)

    if (invalidRoomSelection) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageKey: latestPackageView.package.uniqueKey,
      })
      return
    }

    // Open calendar if no checkIn, or for charter bundle if checkIn doesn't match available charter dates
    if (!checkIn || !isCheckInMatchingCharterDate) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageKey: latestPackageView.package.uniqueKey,
      })

      return
    }

    if (requireCommittedSameRoomSelection) {
      purchaseInFlight.current = true
      purchaseIntentOwnerId.current = currentPurchase.current.customerId || undefined
      setSubmitting(true)
      setPurchaseContextChanged(false)
    }
    try {
      if (!isLoggedIn && !offer.walledGarden) {
        e.preventDefault()

        if (!isMobile) offerPageDispatch({ type: OfferPageActions.closeFilter })

        const hotelOfferDetails: App.AccountModalOfferDetails | undefined = latestPackageView && {
          title: offer.property?.name || latestPackageView.package.name,
          duration: latestPackageView.package.duration,
          value: isFlightBuyType ? latestPackageView.hotelPrice + latestPackageView.flightPrice : latestPackageView.hotelPrice,
          imageId: latestPackageView.package.roomType?.images[0].id,
          imageTitle: offer.property?.name || latestPackageView.package.name,
          discountPercentage: isFlightBuyType ? 0 : latestPackageView.hotelDiscountPercent,
          isBundledWithFlights: isFlightBuyType,
          offer,
          packageView: latestPackageView,
        }
        const result = await showFlashOfferSignUpModal(offer, latestPackageView, {
          initialMode: 'login',
          hotelOfferDetails,
        }, checkIn)

        if (result.loggedIn) {
          if (canContinuePurchase()) await onBookNow(latestPackageView, isFlightBuyType)
        }
        return
      }

      if (canContinuePurchase()) await onBookNow(latestPackageView, isFlightBuyType)
    } finally {
      if (requireCommittedSameRoomSelection) {
        purchaseInFlight.current = false
        purchaseIntentOwnerId.current = undefined
        if (mounted.current) setSubmitting(false)
      }
    }
  }, [canContinuePurchase, requireCommittedSameRoomSelection, sameRoomPurchase, sameRoomReady, triggerExperiencesAnalytics, canPrefetchInFlow, invalidRoomSelection, checkIn, checkOut, duration, packageView, prefetchEnabled, latestPackageView, onSubmit, isLoggedIn, offer, onBookNow, currentCurrency, experienceLists, dispatch, modalElementContext, offerPageDispatch, isMobile, showFlashOfferSignUpModal, rooms, categories, geoCoords, oneClickTransferEnabled, isCheckInMatchingCharterDate])

  return (
    <Group direction="vertical" gap={4}>
      <TextButton {...rest} onClick={onBookNowClick} disabled={disabled || !selectionBurnReady || submitting || ((sameRoomPurchase || requireCommittedSameRoomSelection) && !sameRoomReady) || experiencesPassportFetching || awaitingCharterBundleData || insufficientCharterSeats}>
        {children}
      </TextButton>
      {purchaseContextChanged && <PurchaseContextChangedMessage />}
      {insufficientCharterSeats && (
        <BodyText variant="small" colour="critical">
          {intl.formatMessage({
            id: 'c2bH-Zs44vaIoIqgSTYi',
            defaultMessage: 'Selected flight class is not available for your dates',
            description: 'Error message when charter flight seats are insufficient for selected passengers',
          })}
        </BodyText>
      )}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  airport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  isLoggedIn: isLoggedInSelector(appState),
  customerId: getCurrentUserId(appState),
  offer: state.offer,
  packageOptions: getDurationPackageOptions(state, appState),
  ratesLoading: areRatesLoading(appState, state),
  availablePackageOptions: getAvailableDurationPackageOptions(state, appState),
  rooms: ownProps.overrideRooms ?? state.rooms,
  loyaltyTargetRoomUpgradesSelected: state.loyaltyTargetStayUpgradesSelected,
  isPackageUpgradeLibEnabled: appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croPackageUpgradeLibEnabled],
  charterBundleRoute: state.charterBundleRoute,
  charterBundleEnabled: state.charterBundleEnabled,
  fareClass: state.fareClass,
  // Keyed on the same `state.offer` that supplies the required `offer` prop below, so the button
  // cannot render — let alone be clicked — while this is resolving against a missing offer id.
  bypassRegionFilter: isRegionFilterBypassedForOffer(appState, state.offer?.id),
  bypassRegionSettingId: appState.marketing.bypassRegionSettingId,
}), OfferCheckoutButton)
